// /admin/messages — Zendesk-style ticketing dashboard for patient
// communications. One thread per patient (continuous conversation),
// status/priority/assignment per thread, internal notes interleaved
// with the patient-facing conversation.
//
// Layout:
//   left rail   — filters + thread list (priority sort, unread badge)
//   right pane  — full conversation history + action bar + composer
//                 with two tabs (Reply to patient | Internal note)
//
// State automation lives in the DB trigger bump_thread_on_message:
//   patient sends      → status='open',            assignment cleared
//   admin replies (via patient_messages)
//                      → status='awaiting_patient', assignment kept
// Manual transitions (claim, resolve, reopen, priority) write straight
// to patient_message_threads via the supabase client (admin RLS allows).
// Replies fan out via the admin-thread-reply edge function so the
// admin's full_name lands on the patient bubble.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { AdminLayout } from "@/components/AdminLayout";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import { LoadingScreen } from "@/components/LoadingScreen";

type ThreadStatus   =
  | "open"
  | "awaiting_patient"
  | "awaiting_provider"
  | "on_hold"
  | "pending"
  | "bug_review"
  | "resolved";
type ThreadPriority = "low" | "normal" | "high" | "urgent";

interface ThreadRow {
  id: string;
  patient_id: string;
  ticket_number: number;
  status: ThreadStatus;
  priority: ThreadPriority;
  assigned_admin_id: string | null;
  last_message_at: string;
  patient: { first_name: string; last_name: string } | null;
}

interface ActiveTreatment {
  id: string;
  medication: string;
  dose: string | null;
  monthly_price: number | null;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  stripe_subscription_id: string | null;
  plan_duration_months: number | null;
}

interface PatientContext {
  patient: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    state: string | null;
    created_at: string;
    email: string | null;
    stripe_customer_id: string | null;
    retention_offers_used: number;
  };
  active_treatments: ActiveTreatment[];
}

interface MyReadRow { thread_id: string; last_read_at: string }

interface MacroRow {
  id: string;
  title: string;
  body: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

interface MessageRow {
  id: string;
  patient_id: string;
  direction: "inbound" | "outbound";
  sender_role: string;
  sender_display_name: string | null;
  body: string;
  created_at: string;
}

interface InternalNoteRow {
  id: string;
  thread_id: string;
  patient_id: string;
  author_id: string | null;
  author_name: string | null;
  body: string;
  created_at: string;
  mentioned_admin_ids: string[];
}

interface TeamMember {
  user_id: string;
  email: string | null;
  display_name: string;
  role: string;
}

interface TagRow {
  id: string;
  name: string;
  color: string | null;
}

type FilterId =
  | "all" | "open" | "mine" | "unassigned"
  | "awaiting_patient" | "awaiting_provider"
  | "on_hold" | "resolved";

const FILTERS: Array<{ id: FilterId; label: string }> = [
  { id: "open",              label: "Open" },
  { id: "mine",              label: "Mine" },
  { id: "unassigned",        label: "Unassigned" },
  { id: "awaiting_patient",  label: "Awaiting patient" },
  { id: "awaiting_provider", label: "Awaiting provider" },
  { id: "on_hold",           label: "On hold" },
  { id: "resolved",          label: "Resolved" },
  { id: "all",               label: "All" },
];

// Sort priority: urgent > high > normal > low. Combined with last_message_at
// desc for tiebreak.
const PRIORITY_RANK: Record<ThreadPriority, number> = {
  urgent: 0, high: 1, normal: 2, low: 3,
};

export default function AdminMessages() {
  const { session } = useSession();
  const adminUserId = session?.user.id ?? null;
  const [filter, setFilter] = useState<FilterId>("open");
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  const threadsQ = useThreads();
  const myReadsQ = useMyReads(adminUserId);
  const myReads = myReadsQ.data ?? new Map<string, string>();

  const filteredThreads = useMemo(() => {
    const all = threadsQ.data ?? [];
    let rows = all;
    switch (filter) {
      case "open":              rows = all.filter((t) => t.status === "open"); break;
      case "mine":              rows = all.filter((t) => t.assigned_admin_id === adminUserId); break;
      case "unassigned":        rows = all.filter((t) => !t.assigned_admin_id && t.status !== "resolved"); break;
      case "awaiting_patient":  rows = all.filter((t) => t.status === "awaiting_patient"); break;
      case "awaiting_provider": rows = all.filter((t) => t.status === "awaiting_provider"); break;
      case "on_hold":           rows = all.filter((t) => t.status === "on_hold" || t.status === "pending" || t.status === "bug_review"); break;
      case "resolved":          rows = all.filter((t) => t.status === "resolved"); break;
      case "all":               rows = all; break;
    }
    return rows.slice().sort((a, b) => {
      const pa = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
      if (pa !== 0) return pa;
      return new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime();
    });
  }, [threadsQ.data, filter, adminUserId]);

  // When the filter changes and the current selection drops out of view,
  // clear the selection so the right pane doesn't show a thread that's
  // no longer in the list.
  useEffect(() => {
    if (selectedPatientId && !filteredThreads.find((t) => t.patient_id === selectedPatientId)) {
      setSelectedPatientId(null);
    }
  }, [filter, filteredThreads, selectedPatientId]);

  return (
    <AdminLayout>
      <header className="mb-4">
        <h1 className="font-serif text-[26px] text-fh-ink mb-1">Messages</h1>
        <p className="text-[13px] text-fh-gray m-0">
          Triage patient conversations. New replies appear here within seconds; the
          patient is notified by email when you reply.
        </p>
      </header>

      {/* Single-row pill bar across the full width — separates filtering
          (top) from list+detail (below). Scrolls horizontally on
          narrow viewports so all filters stay reachable. */}
      <FilterTabs
        filter={filter}
        onChange={setFilter}
        counts={countByFilter(threadsQ.data ?? [], adminUserId)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 mt-4">
        <div className="flex flex-col gap-3">
          {threadsQ.isLoading ? (
            <LoadingScreen />
          ) : filteredThreads.length === 0 ? (
            <Card>
              <p className="text-[13px] text-fh-gray m-0 text-center py-6">
                No threads in this view.
              </p>
            </Card>
          ) : (
            <div className="flex flex-col gap-1.5">
              {filteredThreads.map((t) => (
                <ThreadListItem
                  key={t.id}
                  thread={t}
                  unread={!myReads.has(t.id) ||
                    new Date(t.last_message_at).getTime() > new Date(myReads.get(t.id) as string).getTime()}
                  selected={t.patient_id === selectedPatientId}
                  onClick={() => setSelectedPatientId(t.patient_id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0">
          {selectedPatientId ? (
            <ThreadDetail
              key={selectedPatientId}
              patientId={selectedPatientId}
              adminUserId={adminUserId}
            />
          ) : (
            <Card>
              <p className="text-[14px] text-fh-gray m-0 text-center py-12">
                Pick a thread on the left to view.
              </p>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ──────────────────────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────────────────────

function useThreads() {
  // Realtime push (wired in AdminLayout) keeps this fresh — no polling.
  return useQuery<ThreadRow[]>({
    queryKey: ["admin-message-threads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_message_threads")
        .select(`
          id, patient_id, ticket_number, status, priority, assigned_admin_id,
          last_message_at,
          patient:patients(first_name, last_name)
        `)
        .order("last_message_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data as unknown as ThreadRow[]) ?? [];
    },
    refetchOnWindowFocus: true,
  });
}

// Per-admin read state. One row per (admin, thread) once the admin has
// opened that thread at least once.
function useMyReads(adminId: string | null) {
  return useQuery<Map<string, string>>({
    queryKey: ["admin-thread-my-reads", adminId],
    enabled: !!adminId,
    queryFn: async () => {
      if (!adminId) return new Map();
      const { data, error } = await supabase
        .from("admin_thread_reads")
        .select("thread_id, last_read_at")
        .eq("admin_id", adminId)
        .limit(1000);
      if (error) throw error;
      const m = new Map<string, string>();
      for (const r of (data as MyReadRow[] | null) ?? []) m.set(r.thread_id, r.last_read_at);
      return m;
    },
  });
}

// Resolve the current admin's email-reply signature. Order of preference:
//   1. admin_roles.signature override (set via the in-app editor)
//   2. Derived from auth user_metadata.full_name → "— {Name}\nForteHealth Care Team"
//   3. Fallback: "— ForteHealth Care Team"
function useMySignature(adminUserId: string | null) {
  return useQuery({
    queryKey: ["admin-my-signature", adminUserId],
    enabled: !!adminUserId,
    queryFn: async () => {
      if (!adminUserId) return "— ForteHealth Care Team";
      const { data: roleRow } = await supabase
        .from("admin_roles")
        .select("signature")
        .eq("user_id", adminUserId)
        .maybeSingle();
      if (roleRow?.signature?.trim()) return roleRow.signature.trim();
      // Derive default from session metadata.
      const { data: userData } = await supabase.auth.getUser();
      const meta = (userData?.user?.user_metadata ?? {}) as Record<string, unknown>;
      const name =
        (typeof meta.full_name === "string" && meta.full_name.trim()) ||
        (typeof meta.name === "string" && (meta.name as string).trim()) ||
        null;
      return name ? `— ${name}\nForteHealth Care Team` : "— ForteHealth Care Team";
    },
    staleTime: 5 * 60_000,
  });
}

// Substitute {placeholder} tags in a macro body using thread context.
// Unknown tags pass through so admins can hand-fill values the system
// doesn't have (tracking numbers, dates, etc).
function substitutePlaceholders(
  body: string,
  ctx: PatientContext | undefined | null,
  ticketNumber: number | null | undefined,
): string {
  const patient = ctx?.patient;
  const tx = ctx?.active_treatments?.[0]; // most recent active treatment
  const first = patient?.first_name ?? "";
  const last = patient?.last_name ?? "";
  const full = `${first} ${last}`.trim();
  const med = tx?.medication ?? "";
  const dose = tx?.dose ?? "";
  const nextCharge = tx?.current_period_end
    ? new Date(tx.current_period_end).toLocaleDateString(undefined, {
        month: "long", day: "numeric", year: "numeric",
      })
    : "";
  const ticket = ticketNumber ? `#${ticketNumber}` : "";

  const map: Record<string, string> = {
    "{first_name}": first || "there",
    "{last_name}": last,
    "{full_name}": full || "there",
    "{medication}": med || "your medication",
    "{dose}": dose,
    "{next_charge_date}": nextCharge,
    "{ticket_number}": ticket,
  };
  return body.replace(/\{[a-z_]+\}/gi, (m) => map[m] ?? m);
}

function usePatientContext(patientId: string | null) {
  return useQuery<PatientContext | null>({
    queryKey: ["admin-patient-context", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      if (!patientId) return null;
      const { data, error } = await supabase.functions.invoke<
        PatientContext & { error?: string }
      >("admin-get-patient-context", { body: { patient_id: patientId } });
      if (error) throw new Error(error.message ?? "Failed to load patient context");
      if (data?.error) throw new Error(data.error);
      return data as PatientContext;
    },
    staleTime: 60_000,
  });
}

function useAdminTeam() {
  return useQuery<TeamMember[]>({
    queryKey: ["admin-team-list"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{ admins?: TeamMember[]; error?: string }>(
        "admin-list-team",
        { body: {} },
      );
      if (error) throw new Error(error.message ?? "Failed to load admin team");
      if (data?.error) throw new Error(data.error);
      return data?.admins ?? [];
    },
    staleTime: 5 * 60_000, // changes rarely; safe to cache for a while
  });
}

// All tags in the taxonomy. Small taxonomy, cache long.
function useTags() {
  return useQuery<TagRow[]>({
    queryKey: ["admin-message-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("message_thread_tags")
        .select("id, name, color")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data as TagRow[] | null) ?? [];
    },
    staleTime: 60_000,
  });
}

// Tag assignments for a single thread.
function useThreadTags(threadId: string | null) {
  return useQuery<TagRow[]>({
    queryKey: ["admin-thread-tags", threadId],
    enabled: !!threadId,
    queryFn: async () => {
      if (!threadId) return [];
      const { data, error } = await supabase
        .from("message_thread_tag_assignments")
        .select("tag:message_thread_tags(id, name, color)")
        .eq("thread_id", threadId);
      if (error) throw error;
      return ((data as unknown as Array<{ tag: TagRow }> | null) ?? [])
        .map((r) => r.tag).filter((t): t is TagRow => !!t);
    },
  });
}

function useMacros() {
  return useQuery<MacroRow[]>({
    queryKey: ["admin-message-macros"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("message_macros")
        .select("id, title, body, created_by, created_by_name, created_at")
        .order("title", { ascending: true });
      if (error) throw error;
      return (data as MacroRow[] | null) ?? [];
    },
    staleTime: 60_000,
  });
}

function useThreadMessages(patientId: string | null) {
  return useQuery<MessageRow[]>({
    queryKey: ["admin-thread-messages", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error } = await supabase
        .from("patient_messages")
        .select("id, patient_id, direction, sender_role, sender_display_name, body, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as unknown as MessageRow[]) ?? [];
    },
  });
}

function useThreadNotes(patientId: string | null) {
  return useQuery<InternalNoteRow[]>({
    queryKey: ["admin-thread-notes", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error } = await supabase
        .from("thread_internal_notes")
        .select("id, thread_id, patient_id, author_id, author_name, body, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as unknown as InternalNoteRow[]) ?? [];
    },
  });
}

// ──────────────────────────────────────────────────────────────────
// Filter tabs + counts
// ──────────────────────────────────────────────────────────────────

function countByFilter(threads: ThreadRow[], adminUserId: string | null): Record<FilterId, number> {
  return {
    all:               threads.length,
    open:              threads.filter((t) => t.status === "open").length,
    mine:              threads.filter((t) => t.assigned_admin_id === adminUserId).length,
    unassigned:        threads.filter((t) => !t.assigned_admin_id && t.status !== "resolved").length,
    awaiting_patient:  threads.filter((t) => t.status === "awaiting_patient").length,
    awaiting_provider: threads.filter((t) => t.status === "awaiting_provider").length,
    on_hold:           threads.filter((t) =>
      t.status === "on_hold" || t.status === "pending" || t.status === "bug_review").length,
    resolved:          threads.filter((t) => t.status === "resolved").length,
  };
}

function FilterTabs({
  filter, onChange, counts,
}: {
  filter: FilterId;
  onChange: (f: FilterId) => void;
  counts: Record<FilterId, number>;
}) {
  return (
    <div
      role="tablist"
      aria-label="Filter threads"
      className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1 -mb-1"
    >
      {FILTERS.map((f) => {
        const active = filter === f.id;
        return (
          <button
            key={f.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(f.id)}
            className={[
              "shrink-0 inline-flex items-center gap-2 px-4 py-2 text-[13px] font-semibold rounded-full border transition",
              active
                ? "bg-fh-green-dark text-white border-fh-green-dark shadow-sm"
                : "bg-white text-fh-gray border-fh-border hover:border-fh-green hover:text-fh-ink",
            ].join(" ")}
          >
            <span>{f.label}</span>
            <span className={[
              "inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-bold leading-none",
              active ? "bg-white/20 text-white" : "bg-fh-sand text-fh-gray",
            ].join(" ")}>
              {counts[f.id]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Thread list item
// ──────────────────────────────────────────────────────────────────

function ThreadListItem({
  thread, unread, selected, onClick,
}: { thread: ThreadRow; unread: boolean; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "text-left w-full p-3 rounded-[10px] border transition bg-white",
        selected
          ? "border-fh-green shadow-card"
          : "border-fh-border hover:border-fh-green-light",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[14px] text-fh-ink truncate">
            {thread.patient?.first_name} {thread.patient?.last_name}
          </div>
          <div className="text-[11px] text-fh-gray-light font-mono mt-0.5">
            #{thread.ticket_number}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <PriorityChip priority={thread.priority} />
          {unread && (
            <span aria-label="unread" className="w-2 h-2 rounded-full bg-fh-green-dark" />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-[12px] text-fh-gray">
        <StatusBadge status={thread.status} />
        <span>·</span>
        <span>{timeAgo(thread.last_message_at)}</span>
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────
// Thread detail
// ──────────────────────────────────────────────────────────────────

function ThreadDetail({
  patientId, adminUserId,
}: { patientId: string; adminUserId: string | null }) {
  const qc = useQueryClient();
  const messagesQ = useThreadMessages(patientId);
  const notesQ = useThreadNotes(patientId);
  const threadsQ = useThreads();
  const contextQ = usePatientContext(patientId);
  const sigQ = useMySignature(adminUserId);
  const thread = threadsQ.data?.find((t) => t.patient_id === patientId) ?? null;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);

  // Patient display name — prefer the patient_context fetch (resolved
  // via service role) over the thread embed, which can come back null
  // if the patients-table RLS doesn't allow a PostgREST join.
  const patientName = (() => {
    const ctx = contextQ.data?.patient;
    if (ctx?.first_name || ctx?.last_name) return `${ctx.first_name ?? ""} ${ctx.last_name ?? ""}`.trim();
    if (thread?.patient?.first_name || thread?.patient?.last_name)
      return `${thread.patient.first_name ?? ""} ${thread.patient.last_name ?? ""}`.trim();
    return "Patient";
  })();

  const [composeMode, setComposeMode] = useState<"reply" | "note">("reply");
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [showMacroPicker, setShowMacroPicker] = useState(false);
  const [showMacroManager, setShowMacroManager] = useState(false);
  // @mention picker (notes only). mentionAnchor is the index in `draft`
  // of the literal "@" that opened the picker — we splice the chosen
  // name back in from that position.
  const [mentionAnchor, setMentionAnchor] = useState<number | null>(null);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const teamQ = useAdminTeam();

  // Filtered + capped team list for the mention picker. Recomputed
  // whenever the filter or team list changes; the textarea keyboard
  // handler uses this same list so arrow nav stays in sync with what
  // the picker renders.
  const mentionMatches = useMemo(() => {
    const f = mentionFilter.toLowerCase();
    return (teamQ.data ?? [])
      .filter((m) => m.display_name.toLowerCase().includes(f))
      .slice(0, 8);
  }, [teamQ.data, mentionFilter]);

  // Reset selection on every reopen / filter change. Clamp to bounds.
  useEffect(() => { setMentionIndex(0); }, [mentionAnchor, mentionFilter]);

  // Auto-prefill the draft with the admin's signature when entering
  // Reply mode with an empty composer. Cursor lands at the top so the
  // admin types above the signature — no need to remember to sign.
  // Skips for Internal Notes (no patient-facing signature there).
  useEffect(() => {
    if (composeMode !== "reply") return;
    if (draft.trim().length > 0) return;
    const sig = sigQ.data;
    if (!sig) return;
    const next = `\n\n${sig}`;
    setDraft(next);
    requestAnimationFrame(() => {
      const ta = draftRef.current;
      if (ta) { ta.focus(); ta.setSelectionRange(0, 0); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeMode, sigQ.data, patientId]);

  function applyMention(member: TeamMember) {
    if (mentionAnchor === null) return;
    const ta = draftRef.current;
    const caret = ta?.selectionStart ?? draft.length;
    const next =
      draft.slice(0, mentionAnchor) +
      `@${member.display_name} ` +
      draft.slice(caret);
    setDraft(next);
    setMentionAnchor(null);
    setMentionFilter("");
    requestAnimationFrame(() => {
      if (ta) {
        const pos = mentionAnchor + member.display_name.length + 2;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    });
  }

  // Mark this thread "read" for THIS admin only. Upsert into the
  // per-admin reads table — invalidate the local cache so the badge
  // + unread dot update without waiting for realtime to fire.
  useEffect(() => {
    if (!thread?.id || !adminUserId) return;
    (async () => {
      await supabase.from("admin_thread_reads").upsert(
        { admin_id: adminUserId, thread_id: thread.id, last_read_at: new Date().toISOString() },
        { onConflict: "admin_id,thread_id" },
      );
      qc.invalidateQueries({ queryKey: ["admin-thread-my-reads", adminUserId] });
      qc.invalidateQueries({ queryKey: ["admin-thread-unread", adminUserId] });
    })();
  // intentional: only fire when patient changes — not when thread row mutates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, thread?.id, adminUserId]);

  // Interleave patient messages + internal notes by created_at.
  const items = useMemo(() => {
    const msgs = (messagesQ.data ?? []).map((m) => ({ kind: "msg" as const, at: m.created_at, msg: m, id: m.id }));
    const notes = (notesQ.data ?? []).map((n) => ({ kind: "note" as const, at: n.created_at, note: n, id: n.id }));
    return [...msgs, ...notes].sort((a, b) =>
      new Date(a.at).getTime() - new Date(b.at).getTime()
    );
  }, [messagesQ.data, notesQ.data]);

  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [items.length]);

  const send = useMutation<{ ok: true }, Error, void>({
    mutationFn: async () => {
      const body = draft.trim();
      if (!body) throw new Error("Type something first.");
      const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
        "admin-thread-reply",
        { body: { patient_id: patientId, body, internal_note: composeMode === "note" } },
      );
      if (error) throw new Error(error.message ?? "Send failed");
      if (data?.error) throw new Error(data.error);
      return { ok: true };
    },
    onSuccess: () => {
      setDraft("");
      setSendError(null);
      qc.invalidateQueries({ queryKey: ["admin-thread-messages", patientId] });
      qc.invalidateQueries({ queryKey: ["admin-thread-notes", patientId] });
      qc.invalidateQueries({ queryKey: ["admin-message-threads"] });
    },
    onError: (e) => setSendError(e.message),
  });

  async function updateThread(patch: Partial<Pick<ThreadRow, "status" | "priority" | "assigned_admin_id">>) {
    if (!thread?.id) return;
    const { error } = await supabase
      .from("patient_message_threads")
      .update(patch)
      .eq("id", thread.id);
    if (error) {
      setSendError(error.message);
      return;
    }
    qc.invalidateQueries({ queryKey: ["admin-message-threads"] });
  }

  if (!thread) {
    return (
      <Card>
        <p className="text-[13px] text-fh-gray m-0 text-center py-6">
          Loading thread…
        </p>
      </Card>
    );
  }

  const isMine = thread.assigned_admin_id === adminUserId;

  return (
    <Card>
      {/* Action bar */}
      <div className="flex items-start justify-between gap-3 flex-wrap pb-3 mb-3 border-b border-fh-border">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="text-[18px] font-serif text-fh-ink">{patientName}</div>
            <div className="text-[12px] font-mono text-fh-gray-light">#{thread.ticket_number}</div>
          </div>
          <div className="text-[12px] text-fh-gray mt-0.5">
            Last activity {timeAgo(thread.last_message_at)}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isMine && (
            <Button onClick={() => updateThread({ assigned_admin_id: adminUserId })}>
              {thread.assigned_admin_id ? "Take over" : "Assign to me"}
            </Button>
          )}
          <AssigneeSelect
            value={thread.assigned_admin_id}
            onChange={(v) => updateThread({ assigned_admin_id: v })}
          />
          <select
            value={thread.status}
            onChange={(e) => updateThread({ status: e.target.value as ThreadStatus })}
            className="text-[12.5px] font-semibold border border-fh-border rounded-[8px] px-2.5 py-1.5 bg-white text-fh-ink"
          >
            <option value="open">Open</option>
            <option value="awaiting_patient">Awaiting patient</option>
            <option value="awaiting_provider">Awaiting provider</option>
            <option value="pending">Pending</option>
            <option value="on_hold">On hold</option>
            <option value="bug_review">Bug review</option>
            <option value="resolved">Resolved</option>
          </select>
          <select
            value={thread.priority}
            onChange={(e) => updateThread({ priority: e.target.value as ThreadPriority })}
            className="text-[12.5px] font-semibold border border-fh-border rounded-[8px] px-2.5 py-1.5 bg-white text-fh-ink"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      {/* Tag chips for this thread + a "+ Add tag" affordance. */}
      <ThreadTagsBar threadId={thread.id} />

      {/* Patient context — name/email/phone + active treatments. Helps
          the admin answer most questions without bouncing to the Users
          tab. Fetched server-side via admin-get-patient-context so the
          email + treatment join happens once. */}
      <PatientContextPanel patientId={patientId} />

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="overflow-y-auto flex flex-col gap-3 pr-1"
        style={{ maxHeight: "55vh", minHeight: "40vh" }}
      >
        {messagesQ.isLoading ? (
          <div className="text-[13px] text-fh-gray text-center py-12">Loading conversation…</div>
        ) : items.length === 0 ? (
          <div className="text-[13px] text-fh-gray text-center py-12">
            No messages yet. The first reply you send will start the conversation.
          </div>
        ) : (
          items.map((item) =>
            item.kind === "msg" ? (
              <MessageBubble key={item.id} msg={item.msg} />
            ) : (
              <NoteBubble
                key={item.id}
                note={item.note}
                team={teamQ.data ?? []}
                currentAdminId={adminUserId}
              />
            ),
          )
        )}
      </div>

      {/* Composer */}
      <div className="mt-4 pt-4 border-t border-fh-border relative">
        <div className="flex gap-1 mb-2">
          <ComposeTab active={composeMode === "reply"} onClick={() => setComposeMode("reply")}>
            Reply to patient
          </ComposeTab>
          <ComposeTab active={composeMode === "note"} onClick={() => setComposeMode("note")}>
            Internal note
          </ComposeTab>
        </div>

        {sendError && (
          <div className="mb-2"><Alert tone="error">{sendError}</Alert></div>
        )}

        {/* Macro toolbar — only useful when replying to the patient.
            Internal notes don't need canned text. */}
        {composeMode === "reply" && (
          <div className="mb-2 relative">
            <button
              type="button"
              onClick={() => setShowMacroPicker((s) => !s)}
              className="text-[12px] font-semibold text-fh-gray hover:text-fh-ink border border-fh-border rounded-[8px] px-2.5 py-1 bg-white"
            >
              Insert macro ▾
            </button>
            <button
              type="button"
              onClick={() => setShowMacroManager(true)}
              className="ml-2 text-[12px] text-fh-gray hover:text-fh-ink underline"
            >
              Manage
            </button>
            <span className="mx-2 text-fh-gray-light">·</span>
            <button
              type="button"
              onClick={() => setShowSignatureEditor(true)}
              className="text-[12px] text-fh-gray hover:text-fh-ink underline"
            >
              Edit signature
            </button>
            {showMacroPicker && (
              <MacroPicker
                onPick={(macro) => {
                  const expanded = substitutePlaceholders(
                    macro.body,
                    contextQ.data,
                    thread?.ticket_number ?? null,
                  );
                  // If the draft is currently nothing but the auto-
                  // prefilled signature, splice the macro ABOVE it
                  // (signature stays at the bottom of the reply).
                  // Otherwise honor the caret position.
                  const sig = sigQ.data ?? "";
                  if (sig && draft.trim() === sig.trim()) {
                    const next = `${expanded}\n\n${sig}`;
                    setDraft(next);
                    requestAnimationFrame(() => {
                      const ta = draftRef.current;
                      if (ta) {
                        ta.focus();
                        ta.setSelectionRange(expanded.length, expanded.length);
                      }
                    });
                  } else {
                    insertAtCursor(draftRef.current, expanded, setDraft);
                  }
                  setShowMacroPicker(false);
                }}
                onClose={() => setShowMacroPicker(false)}
              />
            )}
          </div>
        )}

        <textarea
          ref={draftRef}
          value={draft}
          onChange={(e) => {
            const v = e.target.value.slice(0, 10000);
            setDraft(v);
            // Notes-only @ picker. Detect the current word the caret
            // sits on; if it starts with @ and is bounded by whitespace,
            // open the picker and stream the partial as filter.
            if (composeMode === "note") {
              const caret = e.target.selectionStart ?? v.length;
              const before = v.slice(0, caret);
              const at = before.lastIndexOf("@");
              if (at >= 0) {
                const tokenStart = at;
                const tokenChar = at === 0 ? " " : before[at - 1] ?? " ";
                const isBoundary = at === 0 || /\s|^\(|\[|,|\./.test(tokenChar);
                const partial = before.slice(at + 1);
                // Only treat as a mention while the partial is name-ish
                // (no spaces past 20 chars) — bail out gracefully.
                if (isBoundary && partial.length <= 40 && !partial.includes("\n")) {
                  setMentionAnchor(tokenStart);
                  setMentionFilter(partial);
                  return;
                }
              }
              setMentionAnchor(null);
            }
          }}
          onKeyDown={(e) => {
            // Mention picker open: arrow keys cycle, Enter picks, Esc closes.
            if (mentionAnchor !== null && mentionMatches.length > 0) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setMentionIndex((i) => (i + 1) % mentionMatches.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                return;
              }
              if (e.key === "Enter" && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                const pick = mentionMatches[mentionIndex] ?? mentionMatches[0];
                if (pick) applyMention(pick);
                return;
              }
              if (e.key === "Tab") {
                e.preventDefault();
                const pick = mentionMatches[mentionIndex] ?? mentionMatches[0];
                if (pick) applyMention(pick);
                return;
              }
            }
            if (mentionAnchor !== null && e.key === "Escape") {
              setMentionAnchor(null);
              return;
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && draft.trim()) {
              e.preventDefault();
              send.mutate();
            }
          }}
          rows={4}
          placeholder={
            composeMode === "reply"
              ? "Type your reply to the patient…"
              : "Internal note — visible only to admins on this thread."
          }
          className={[
            "w-full px-3 py-2.5 text-[14px] text-fh-ink border rounded-[10px] focus:outline-none focus:ring-2 resize-y",
            composeMode === "note"
              ? "bg-amber-50/40 border-amber-300/60 focus:ring-amber-400 focus:border-amber-400"
              : "bg-white border-fh-border focus:ring-fh-green focus:border-fh-green",
          ].join(" ")}
        />
        {composeMode === "note" && mentionAnchor !== null && (
          <MentionPicker
            matches={mentionMatches}
            selectedIndex={mentionIndex}
            onHover={setMentionIndex}
            onPick={applyMention}
            onClose={() => setMentionAnchor(null)}
          />
        )}
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11.5px] text-fh-gray-light">
            <kbd className="px-1 py-0.5 rounded border border-fh-border text-[10px] font-mono">⌘ Enter</kbd>{" "}
            to send · {composeMode === "note"
              ? "Internal — patient won't see this."
              : "Patient gets an email and sees the reply in their portal."}
          </span>
          <Button
            loading={send.isPending}
            disabled={!draft.trim()}
            onClick={() => send.mutate()}
          >
            {composeMode === "note" ? "Save note" : "Send reply"}
          </Button>
        </div>
      </div>

      {showMacroManager && (
        <MacroManagerModal onClose={() => setShowMacroManager(false)} adminUserId={adminUserId} />
      )}
      {showSignatureEditor && adminUserId && (
        <SignatureEditorModal
          adminUserId={adminUserId}
          currentSignature={sigQ.data ?? ""}
          onClose={() => setShowSignatureEditor(false)}
        />
      )}
    </Card>
  );
}

function SignatureEditorModal({
  adminUserId, currentSignature, onClose,
}: { adminUserId: string; currentSignature: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(currentSignature);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const trimmed = value.trim();
      // Empty string clears the override — UI falls back to derived default.
      const { error: e } = await supabase
        .from("admin_roles")
        .update({ signature: trimmed || null })
        .eq("user_id", adminUserId);
      if (e) throw e;
      qc.invalidateQueries({ queryKey: ["admin-my-signature", adminUserId] });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 18, 0.55)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[14px] shadow-2xl w-full max-w-[520px] p-5"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="font-serif text-[20px] text-fh-ink mb-0">Your reply signature</h2>
            <p className="text-[12.5px] text-fh-gray mt-0.5 mb-0">
              Auto-appended to every patient-facing reply you compose. Visible to the patient.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-fh-gray hover:text-fh-ink text-[22px] leading-none">×</button>
        </div>
        {error && <div className="mb-3"><Alert tone="error">{error}</Alert></div>}
        <textarea
          rows={4}
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, 500))}
          placeholder="— Sam Smith, NP&#10;ForteHealth Care Team"
          className="w-full px-3 py-2 text-[13.5px] border border-fh-border rounded-[8px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green"
        />
        <p className="mt-2 text-[11.5px] text-fh-gray-light">
          Leave blank to use the default ("— {"{your name}"}\nForteHealth Care Team").
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button loading={saving} onClick={save}>Save signature</Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Macros
// ──────────────────────────────────────────────────────────────────

function MacroPicker({
  onPick, onClose,
}: { onPick: (m: MacroRow) => void; onClose: () => void }) {
  const macrosQ = useMacros();
  // Close when the user clicks outside.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-macro-picker]") && !target.closest("[data-macro-trigger]")) {
        onClose();
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  return (
    <div
      data-macro-picker
      className="absolute z-30 mt-1 w-[360px] max-h-[280px] overflow-y-auto bg-white border border-fh-border rounded-[10px] shadow-card"
    >
      {macrosQ.isLoading ? (
        <div className="px-3 py-3 text-[12.5px] text-fh-gray">Loading…</div>
      ) : (macrosQ.data ?? []).length === 0 ? (
        <div className="px-3 py-3 text-[12.5px] text-fh-gray">
          No macros yet. Click <strong>Manage</strong> to add one.
        </div>
      ) : (
        (macrosQ.data ?? []).map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onPick(m)}
            className="block w-full text-left px-3 py-2 hover:bg-fh-sand border-b border-fh-border last:border-b-0"
          >
            <div className="text-[13px] font-semibold text-fh-ink">{m.title}</div>
            <div className="text-[11.5px] text-fh-gray truncate">{m.body}</div>
          </button>
        ))
      )}
    </div>
  );
}

function MacroManagerModal({
  onClose, adminUserId,
}: { onClose: () => void; adminUserId: string | null }) {
  const qc = useQueryClient();
  const macrosQ = useMacros();
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDraftTitle("");
    setDraftBody("");
    setEditingId(null);
  }

  async function save() {
    setError(null);
    const title = draftTitle.trim();
    const body = draftBody.trim();
    if (!title || !body) {
      setError("Title and body are required.");
      return;
    }
    if (editingId) {
      const { error: e } = await supabase
        .from("message_macros")
        .update({ title, body, updated_at: new Date().toISOString() })
        .eq("id", editingId);
      if (e) { setError(e.message); return; }
    } else {
      const { error: e } = await supabase
        .from("message_macros")
        .insert({ title, body, created_by: adminUserId ?? null });
      if (e) { setError(e.message); return; }
    }
    qc.invalidateQueries({ queryKey: ["admin-message-macros"] });
    reset();
  }

  async function remove(id: string) {
    if (!confirm("Delete this macro? This affects everyone on the admin team.")) return;
    const { error: e } = await supabase.from("message_macros").delete().eq("id", id);
    if (e) { setError(e.message); return; }
    qc.invalidateQueries({ queryKey: ["admin-message-macros"] });
    if (editingId === id) reset();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 18, 0.55)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[14px] shadow-2xl w-full max-w-[600px] max-h-[88vh] flex flex-col"
      >
        <div className="px-5 pt-5 pb-3 border-b border-fh-border flex items-start justify-between">
          <div>
            <h2 className="font-serif text-[20px] text-fh-ink mb-0">Manage macros</h2>
            <p className="text-[12.5px] text-fh-gray mt-0.5 mb-0">
              Shared canned replies. Everyone on the admin team sees and can use these.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-fh-gray hover:text-fh-ink text-[22px] leading-none -mt-1">×</button>
        </div>

        <div className="overflow-y-auto p-5 flex-1">
          {error && <div className="mb-3"><Alert tone="error">{error}</Alert></div>}

          <div className="flex flex-col gap-2 mb-4">
            {(macrosQ.data ?? []).map((m) => (
              <div key={m.id} className="border border-fh-border rounded-[8px] p-3 bg-fh-cream/50">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-semibold text-fh-ink">{m.title}</div>
                    <div className="text-[12px] text-fh-gray whitespace-pre-wrap mt-0.5">{m.body}</div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => { setEditingId(m.id); setDraftTitle(m.title); setDraftBody(m.body); }}
                      className="text-[11.5px] font-semibold text-fh-green hover:text-fh-green-dark underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(m.id)}
                      className="text-[11.5px] font-semibold text-fh-gray hover:text-red-700 underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {(macrosQ.data ?? []).length === 0 && (
              <p className="text-[13px] text-fh-gray m-0 text-center py-4">No macros yet.</p>
            )}
          </div>

          <div className="border-t border-fh-border pt-4">
            <div className="text-[12px] font-semibold uppercase tracking-wider text-fh-gray mb-2">
              {editingId ? "Edit macro" : "New macro"}
            </div>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value.slice(0, 80))}
              placeholder="Title (e.g. Welcome reply)"
              className="w-full px-3 py-2 text-[13.5px] border border-fh-border rounded-[8px] mb-2 focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green"
            />
            <textarea
              rows={4}
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value.slice(0, 5000))}
              placeholder="Body — the text inserted into the composer."
              className="w-full px-3 py-2 text-[13.5px] border border-fh-border rounded-[8px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green"
            />
            <div className="mt-3 flex items-center gap-2">
              <Button onClick={save}>{editingId ? "Save changes" : "Add macro"}</Button>
              {editingId && <Button variant="ghost" onClick={reset}>Cancel</Button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Insert text at the textarea's caret (or replace selection). Falls back
// to appending when the ref isn't available.
function insertAtCursor(
  ta: HTMLTextAreaElement | null,
  insert: string,
  setDraft: (v: string) => void,
): void {
  if (!ta) { setDraft(insert); return; }
  const start = ta.selectionStart ?? ta.value.length;
  const end = ta.selectionEnd ?? ta.value.length;
  const next = ta.value.slice(0, start) + insert + ta.value.slice(end);
  setDraft(next);
  // Restore caret right after the inserted text on the next paint.
  requestAnimationFrame(() => {
    ta.focus();
    const pos = start + insert.length;
    ta.setSelectionRange(pos, pos);
  });
}

// ──────────────────────────────────────────────────────────────────
// Bubbles
// ──────────────────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: MessageRow }) {
  const fromAdmin = msg.direction === "inbound"; // care_team → patient
  const fromPatient = msg.direction === "outbound";
  const senderLabel = fromAdmin
    ? msg.sender_display_name || "Care Team"
    : "Patient";
  return (
    <div className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[80%] rounded-[14px] px-3.5 py-2 leading-relaxed border",
          fromAdmin
            ? "bg-fh-green-dark text-white border-fh-green-dark"
            : "bg-white text-fh-ink border-fh-border",
        ].join(" ")}
      >
        <div className={`text-[11px] font-semibold mb-0.5 ${fromAdmin ? "text-white/80" : "text-fh-green-dark"}`}>
          {senderLabel}{fromPatient ? "" : ""}
        </div>
        <div className="text-[13.5px] whitespace-pre-wrap break-words">{msg.body}</div>
        <div className={`mt-1 text-[10.5px] ${fromAdmin ? "text-white/65" : "text-fh-gray"}`}>
          {formatTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

function PatientContextPanel({ patientId }: { patientId: string }) {
  const ctxQ = usePatientContext(patientId);
  if (ctxQ.isLoading) {
    return (
      <div className="mb-3 bg-fh-cream/60 border border-fh-border rounded-[10px] px-3.5 py-2.5 text-[12.5px] text-fh-gray">
        Loading patient context…
      </div>
    );
  }
  if (ctxQ.error || !ctxQ.data) {
    return (
      <div className="mb-3 bg-red-50 border border-red-200 rounded-[10px] px-3.5 py-2.5 text-[12.5px] text-red-800">
        Couldn't load patient context: {(ctxQ.error as Error | null)?.message ?? "unknown"}
      </div>
    );
  }
  const { patient, active_treatments } = ctxQ.data;
  return (
    <div className="mb-3 bg-fh-cream/60 border border-fh-border rounded-[10px] px-4 py-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-[12.5px]">
        <ContextField label="Email" value={patient.email ?? "—"} mono />
        <ContextField label="Phone" value={patient.phone ?? "—"} />
        <ContextField label="State" value={patient.state ?? "—"} />
        <ContextField
          label="Member since"
          value={new Date(patient.created_at).toLocaleDateString()}
        />
      </div>
      {active_treatments.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-fh-border">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-fh-gray mb-1.5">
            Active subscriptions
          </div>
          <ul className="flex flex-col gap-1.5">
            {active_treatments.map((t) => (
              <li key={t.id} className="flex items-start justify-between gap-3 text-[12.5px]">
                <div className="min-w-0">
                  <div className="font-semibold text-fh-ink">
                    {t.medication}{t.dose ? ` · ${t.dose}` : ""}
                  </div>
                  <div className="text-fh-gray text-[11.5px]">
                    {t.status === "paused" ? "Paused · " : ""}
                    {t.current_period_end
                      ? (t.cancel_at_period_end
                          ? `Cancels ${new Date(t.current_period_end).toLocaleDateString()}`
                          : `Next charge ${new Date(t.current_period_end).toLocaleDateString()}`)
                      : "No upcoming charge"}
                  </div>
                </div>
                <div className="text-right font-semibold text-fh-ink shrink-0">
                  {t.monthly_price != null ? `$${Number(t.monthly_price).toFixed(2)}/mo` : "—"}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-fh-border text-[12px] text-fh-gray italic">
          No active subscriptions.
        </div>
      )}
    </div>
  );
}

function ContextField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-fh-gray">{label}</div>
      <div className={`text-fh-ink font-medium truncate ${mono ? "font-mono text-[12px]" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function NoteBubble({
  note, team, currentAdminId,
}: { note: InternalNoteRow; team: TeamMember[]; currentAdminId: string | null }) {
  // Was the current admin tagged in this note?
  const mentionsMe = !!(currentAdminId && note.mentioned_admin_ids?.includes(currentAdminId));
  return (
    <div className="flex justify-center">
      <div className={[
        "max-w-[90%] w-full rounded-[10px] px-3.5 py-2 border",
        mentionsMe
          ? "bg-amber-100 border-amber-400 shadow-sm"
          : "bg-amber-50 border-amber-300/70",
      ].join(" ")}>
        <div className="flex items-center justify-between text-[10.5px] text-amber-800 font-semibold uppercase tracking-wider mb-0.5">
          <span>
            {mentionsMe ? "@ You · " : ""}Internal note{note.author_name ? ` · ${note.author_name}` : ""}
          </span>
          <span className="font-normal normal-case text-amber-700">{formatTime(note.created_at)}</span>
        </div>
        <div className="text-[13.5px] whitespace-pre-wrap break-words text-amber-950">
          {renderWithMentions(note.body, team, currentAdminId)}
        </div>
      </div>
    </div>
  );
}

// Render note body with @mentions wrapped in a styled span. Greedy
// match longest names first so "@Sam Smith" matches before "@Sam".
function renderWithMentions(
  body: string, team: TeamMember[], currentAdminId: string | null,
): React.ReactNode[] {
  if (!body || !team.length) return [body];
  const sorted = team.slice().sort((a, b) => b.display_name.length - a.display_name.length);
  // Build one alternation regex with name escapes.
  const escaped = sorted.map((m) => m.display_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(${escaped.join("|")})`, "gi");
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const matched = m[1] ?? "";
    const teamMember = team.find((t) => t.display_name.toLowerCase() === matched.toLowerCase());
    const isMe = !!(teamMember && currentAdminId && teamMember.user_id === currentAdminId);
    parts.push(
      <span
        key={m.index}
        className={[
          "inline-block px-1 rounded font-semibold",
          isMe ? "bg-amber-300 text-amber-950" : "bg-amber-200/70 text-amber-900",
        ].join(" ")}
      >
        @{matched}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}

// Assignment dropdown listing every admin on the team + Unassigned.
// Tag bar for the open thread. Shows current tag chips + a "+ Add tag"
// dropdown that lists every tag in the taxonomy (with quick filter +
// inline "Create new" affordance). All writes go through admin RLS.
function ThreadTagsBar({ threadId }: { threadId: string }) {
  const qc = useQueryClient();
  const allTagsQ = useTags();
  const myTagsQ = useThreadTags(threadId);
  const [showPicker, setShowPicker] = useState(false);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  const myTagIds = new Set((myTagsQ.data ?? []).map((t) => t.id));
  const availableTags = (allTagsQ.data ?? [])
    .filter((t) => !myTagIds.has(t.id))
    .filter((t) => t.name.toLowerCase().includes(filter.toLowerCase()));

  async function addTag(tag: TagRow) {
    setError(null);
    const { error: e } = await supabase
      .from("message_thread_tag_assignments")
      .insert({ thread_id: threadId, tag_id: tag.id });
    if (e) { setError(e.message); return; }
    qc.invalidateQueries({ queryKey: ["admin-thread-tags", threadId] });
    setFilter("");
  }

  async function removeTag(tag: TagRow) {
    setError(null);
    const { error: e } = await supabase
      .from("message_thread_tag_assignments")
      .delete()
      .eq("thread_id", threadId)
      .eq("tag_id", tag.id);
    if (e) { setError(e.message); return; }
    qc.invalidateQueries({ queryKey: ["admin-thread-tags", threadId] });
  }

  async function createAndAdd() {
    const name = filter.trim().toLowerCase();
    if (!name) return;
    setError(null);
    // Try to insert; if it conflicts (unique violation), look it up.
    const { data: inserted, error: insErr } = await supabase
      .from("message_thread_tags")
      .insert({ name })
      .select("id, name, color")
      .maybeSingle();
    let tag = inserted as TagRow | null;
    if (insErr) {
      // Probably a conflict — fall back to lookup.
      const { data: existing } = await supabase
        .from("message_thread_tags")
        .select("id, name, color")
        .eq("name", name)
        .maybeSingle();
      tag = existing as TagRow | null;
      if (!tag) { setError(insErr.message); return; }
    }
    qc.invalidateQueries({ queryKey: ["admin-message-tags"] });
    if (tag) await addTag(tag);
  }

  return (
    <div className="flex items-center gap-2 flex-wrap mb-3 relative">
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-fh-gray">
        Tags
      </span>
      {(myTagsQ.data ?? []).map((t) => (
        <TagChip key={t.id} tag={t} onRemove={() => removeTag(t)} />
      ))}
      <button
        type="button"
        onClick={() => setShowPicker((s) => !s)}
        className="text-[11.5px] font-semibold border border-dashed border-fh-border rounded-full px-2.5 py-0.5 text-fh-gray hover:border-fh-green hover:text-fh-ink"
      >
        + Add tag
      </button>
      {error && <span className="text-[11px] text-red-700">{error}</span>}

      {showPicker && (
        <div
          data-tag-picker
          onMouseLeave={() => setShowPicker(false)}
          className="absolute z-30 top-full mt-1 left-0 w-[300px] bg-white border border-fh-border rounded-[10px] shadow-card p-2"
        >
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter or create…"
            className="w-full px-2.5 py-1.5 text-[12.5px] border border-fh-border rounded-[6px] mb-1 focus:outline-none focus:ring-1 focus:ring-fh-green"
            autoFocus
          />
          <div className="max-h-[200px] overflow-y-auto">
            {availableTags.length === 0 && !filter.trim() && (
              <div className="px-2 py-2 text-[11.5px] text-fh-gray italic">
                No more tags available.
              </div>
            )}
            {availableTags.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { addTag(t); setShowPicker(false); }}
                className="block w-full text-left px-2 py-1.5 hover:bg-fh-sand rounded text-[12.5px]"
              >
                <span
                  className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                  style={{ backgroundColor: t.color ? `#${t.color}` : "#888" }}
                />
                {t.name}
              </button>
            ))}
            {filter.trim() &&
              !(allTagsQ.data ?? []).some(
                (t) => t.name.toLowerCase() === filter.trim().toLowerCase(),
              ) && (
                <button
                  type="button"
                  onClick={() => { createAndAdd(); setShowPicker(false); }}
                  className="block w-full text-left px-2 py-1.5 hover:bg-fh-sand rounded text-[12.5px] text-fh-green-dark font-semibold border-t border-fh-border mt-1"
                >
                  + Create "{filter.trim()}"
                </button>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function TagChip({ tag, onRemove }: { tag: TagRow; onRemove?: () => void }) {
  const bg = tag.color ? `#${tag.color}1A` : "#88888820"; // ~10% alpha
  const fg = tag.color ? `#${tag.color}` : "#525252";
  return (
    <span
      className="inline-flex items-center gap-1 text-[11.5px] font-semibold rounded-full px-2 py-0.5"
      style={{ backgroundColor: bg, color: fg }}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${tag.name}`}
          className="opacity-70 hover:opacity-100"
          style={{ color: fg }}
        >×</button>
      )}
    </span>
  );
}

function AssigneeSelect({
  value, onChange,
}: { value: string | null; onChange: (v: string | null) => void }) {
  const teamQ = useAdminTeam();
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="text-[12.5px] font-semibold border border-fh-border rounded-[8px] px-2.5 py-1.5 bg-white text-fh-ink min-w-[140px]"
    >
      <option value="">Unassigned</option>
      {(teamQ.data ?? []).map((m) => (
        <option key={m.user_id} value={m.user_id}>{m.display_name}</option>
      ))}
    </select>
  );
}

// @mention picker. Appears below the textarea while the caret is on
// an "@<partial>" token. Drives selection from the parent so the
// textarea's keyboard handler can cycle (↑/↓), confirm (Enter / Tab),
// or dismiss (Esc) without losing focus.
function MentionPicker({
  matches, selectedIndex, onHover, onPick, onClose,
}: {
  matches: TeamMember[];
  selectedIndex: number;
  onHover: (i: number) => void;
  onPick: (m: TeamMember) => void;
  onClose: () => void;
}) {
  // Close on outside click (not when clicking inside the picker or the textarea).
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-mention-picker]") && !target.closest("textarea")) {
        onClose();
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  // Keep the highlighted row scrolled into view when arrowing through.
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (matches.length === 0) return null;
  return (
    <div
      data-mention-picker
      className="absolute z-30 bottom-[110px] left-3 w-[280px] max-h-[240px] overflow-y-auto bg-white border border-fh-border rounded-[10px] shadow-card"
    >
      {matches.map((m, i) => {
        const active = i === selectedIndex;
        return (
          <button
            key={m.user_id}
            ref={active ? selectedRef : undefined}
            type="button"
            // Pointer events drive selection so click-to-pick still works,
            // and hover keeps the keyboard + mouse highlight in sync.
            onMouseEnter={() => onHover(i)}
            onMouseDown={(e) => {
              // Prevent the textarea from losing focus.
              e.preventDefault();
              onPick(m);
            }}
            className={[
              "block w-full text-left px-3 py-2 border-b border-fh-border last:border-b-0 transition",
              active ? "bg-fh-sand" : "hover:bg-fh-sand/60",
            ].join(" ")}
          >
            <div className="text-[13px] font-semibold text-fh-ink">{m.display_name}</div>
            <div className="text-[11px] text-fh-gray truncate">{m.email ?? m.role}</div>
          </button>
        );
      })}
      <div className="px-3 py-1.5 text-[10.5px] text-fh-gray-light border-t border-fh-border bg-fh-cream/40">
        ↑ ↓ to navigate · Enter to pick · Esc to close
      </div>
    </div>
  );
}

function ComposeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-3 py-1.5 text-[12.5px] font-semibold rounded-t-[8px] border-b-2 transition",
        active
          ? "text-fh-ink border-fh-green-dark bg-white"
          : "text-fh-gray border-transparent hover:text-fh-ink",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────
// Chips
// ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ThreadStatus }) {
  const map: Record<ThreadStatus, { label: string; cls: string }> = {
    open:              { label: "Open",              cls: "bg-fh-green-light/20 text-fh-green-dark" },
    awaiting_patient:  { label: "Awaiting patient",  cls: "bg-fh-sand text-fh-gray" },
    awaiting_provider: { label: "Awaiting provider", cls: "bg-violet-100 text-violet-700" },
    on_hold:           { label: "On hold",           cls: "bg-slate-200 text-slate-700" },
    pending:           { label: "Pending",           cls: "bg-amber-100 text-amber-700" },
    bug_review:        { label: "Bug review",        cls: "bg-rose-100 text-rose-700" },
    resolved:          { label: "Resolved",          cls: "bg-emerald-100 text-emerald-700" },
  };
  const m = map[status] ?? { label: status, cls: "bg-fh-sand text-fh-gray" };
  return (
    <span className={`text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${m.cls}`}>
      {m.label}
    </span>
  );
}

function PriorityChip({ priority }: { priority: ThreadPriority }) {
  if (priority === "normal") return null;
  const map: Record<ThreadPriority, string> = {
    low:    "bg-fh-sand text-fh-gray",
    normal: "",
    high:   "bg-amber-100 text-amber-800",
    urgent: "bg-red-100 text-red-700",
  };
  return (
    <span className={`text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${map[priority]}`}>
      {priority}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────
// Time helpers
// ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}
